import { VoucherSaleRow } from '../models/shout-plus-brief';

/** Header names stores use for each column we need, lower-cased. */
const COLUMN_ALIASES = {
  orderId: ['name', 'order id', 'order_id', 'order number', 'order no', 'order #', 'order', 'id'],
  code: ['discount code', 'discount codes', 'coupon code', 'coupon codes', 'coupon', 'coupons', 'coupon(s)', 'promo code', 'voucher code', 'code'],
  total: ['total', 'order total', 'grand total', 'total price', 'order total amount', 'amount', 'order_total'],
  currency: ['currency', 'order currency'],
  orderedAt: ['created at', 'order date', 'date', 'paid at', 'created', 'date created'],
} as const;

type Column = keyof typeof COLUMN_ALIASES;

export interface OrderExportParseResult {
  rows: VoucherSaleRow[];
  /** Required columns that couldn't be found in the header row. */
  missing: string[];
}

const COLUMN_LABELS: Record<'orderId' | 'code' | 'total', string> = {
  orderId: 'order number',
  code: 'discount code',
  total: 'order total',
};

/** Split CSV text into rows of cells, handling quoted cells with commas, quotes and line breaks. */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;
  const delimiter = detectDelimiter(text);
  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    if (quoted) {
      if (char === '"' && text[i + 1] === '"') {
        cell += '"';
        i++;
      } else if (char === '"') {
        quoted = false;
      } else {
        cell += char;
      }
    } else if (char === '"') {
      quoted = true;
    } else if (char === delimiter) {
      row.push(cell);
      cell = '';
    } else if (char === '\n' || char === '\r') {
      if (char === '\r' && text[i + 1] === '\n') i++;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
    } else {
      cell += char;
    }
  }
  if (cell || row.length) {
    row.push(cell);
    rows.push(row);
  }
  return rows.filter((cells) => cells.some((value) => value.trim()));
}

/** "£1,234.50", "1.234,50 EUR" and "59.99" all become numbers; anything else is 0. */
export function parseMoney(value: string): number {
  let cleaned = value.replace(/[^\d.,-]/g, '');
  if (/,\d{2}$/.test(cleaned) && !/\.\d{2}$/.test(cleaned)) {
    cleaned = cleaned.replace(/\./g, '').replace(',', '.');
  } else {
    cleaned = cleaned.replace(/,/g, '');
  }
  const amount = Number(cleaned);
  return Number.isFinite(amount) ? amount : 0;
}

/**
 * Read a store's order export (Shopify, WooCommerce or any CSV with an order
 * number, discount code and total) into rows for the sales import.
 */
export function parseOrderExport(text: string): OrderExportParseResult {
  const [header, ...body] = parseCsv(text.replace(/^﻿/, ''));
  if (!header) {
    return { rows: [], missing: Object.values(COLUMN_LABELS) };
  }
  const headings = header.map((cell) => cell.trim().toLowerCase());
  const index = (column: Column) => {
    for (const alias of COLUMN_ALIASES[column]) {
      const found = headings.indexOf(alias);
      if (found !== -1) return found;
    }
    return -1;
  };
  const columns = {
    orderId: index('orderId'),
    code: index('code'),
    total: index('total'),
    currency: index('currency'),
    orderedAt: index('orderedAt'),
  };
  const missing = (Object.keys(COLUMN_LABELS) as (keyof typeof COLUMN_LABELS)[])
    .filter((column) => columns[column] === -1)
    .map((column) => COLUMN_LABELS[column]);
  if (missing.length) {
    return { rows: [], missing };
  }

  const cell = (cells: string[], at: number) => (at === -1 ? '' : (cells[at] ?? '').trim());
  const rows = body
    .map((cells) => ({
      orderId: cell(cells, columns.orderId),
      code: cell(cells, columns.code),
      total: parseMoney(cell(cells, columns.total)),
      currency: cell(cells, columns.currency) || undefined,
      orderedAt: cell(cells, columns.orderedAt) || undefined,
    }))
    .filter((row) => row.orderId);
  return { rows, missing: [] };
}

function detectDelimiter(text: string): string {
  const firstLine = text.slice(0, text.search(/\r?\n/) === -1 ? undefined : text.search(/\r?\n/));
  const counts = [',', ';', '\t'].map((delimiter) => [delimiter, firstLine.split(delimiter).length] as const);
  return counts.sort((a, b) => b[1] - a[1])[0][0];
}
